import dayjs from "dayjs";
import type { IPropertyDetailsResponse } from "@/src-refactor/modules/store/qrtk/services/realestate/responses/property-details.response";
import { C_PROPERTIES_STATUS } from "@/src-refactor/modules/shared/model/const/property/real-estate-status";

export interface IPropertyDetailDataLog {
  expiresProperty: string | null;
  createdProperty: string | null;
  propertyCreator: string | null;
  lastModifiedDate: string | null;
  lastUserModifiedProperty: string | null;
  propertyDroppedDate: string | null;
  lastUserDroppedProperty: string | null;
  propertyPublicationDate: string | null;
  propertyUserPublished: string | null;
  propertyResolveConflictDate: string | null;
  conflictResolver: string | null;
  propertyReactivationDate: string | null;
  propertyOutNegotiationDate: string | null;
  propertyOutNegotiationDays: string | null;
  activated: string | null;
}

export interface IPropertyDataLogItem {
  label: string;
  value: string | null;
  show: boolean;
}

export const prepareDetailPropertyDataLog = (
  data: IPropertyDetailsResponse
): IPropertyDetailDataLog => {
  return {
    expiresProperty: data?.expires
      ? dayjs(data.expires).format("DD/MM/YYYY HH:mm")
      : null,
    createdProperty: data?.created
      ? dayjs(data.created).format("DD/MM/YYYY HH:mm")
      : null,
    propertyCreator: data?.creator || null,
    lastModifiedDate: data?.modified
      ? dayjs(data.modified).format("DD/MM/YYYY HH:mm")
      : null,
    lastUserModifiedProperty: data?.lastmodifier || null,
    propertyDroppedDate: data?.drop_date
      ? dayjs(data.drop_date).format("DD/MM/YYYY HH:mm")
      : null,
    lastUserDroppedProperty: data?.dropper || null,
    propertyPublicationDate: data?.availabled
      ? dayjs(data.availabled).format("DD/MM/YYYY HH:mm")
      : null,
    propertyUserPublished: data?.availableby_name || null,
    propertyResolveConflictDate: data?.resolved_conflict
      ? dayjs(data.resolved_conflict).format("DD/MM/YYYY HH:mm")
      : null,
    conflictResolver: data?.conflict_resolver || null,
    propertyReactivationDate: data?.reactivated
      ? dayjs(data.reactivated).format("DD/MM/YYYY HH:mm")
      : null,
    propertyOutNegotiationDate: data?.business_expire_date
      ? dayjs(data.business_expire_date).format("DD/MM/YYYY HH:mm")
      : null,
    propertyOutNegotiationDays: data?.in_business_days || null,
    activated: data?.activated
      ? dayjs(data.activated).format("DD/MM/YYYY HH:mm")
      : null
  };
};

export const prepareDetailPropertyDataLogItems = (
  dataLog: IPropertyDetailDataLog,
  status: { id: number } | null
): IPropertyDataLogItem[] => {
  const isPropertyStatus = (statusIds: number[]): boolean =>
    status ? statusIds.includes(status.id) : false;

  const showIs = {
    dateExpiration: isPropertyStatus([
      C_PROPERTIES_STATUS.ACTIVE.id,
      C_PROPERTIES_STATUS.INNEGOTIATION.id,
      C_PROPERTIES_STATUS.DROPPED.id,
      C_PROPERTIES_STATUS.INCONFLICT.id,
      C_PROPERTIES_STATUS.PRECAPTURED.id
    ]),
    dateCreation: true,
    creatorProperty: true,
    lastModificationDate: true,
    lastUserModificationProperty: true,
    dateDropProperty: isPropertyStatus([C_PROPERTIES_STATUS.DROPPED.id]),
    userDropProperty: isPropertyStatus([C_PROPERTIES_STATUS.DROPPED.id]),
    datePublicationProperty: isPropertyStatus([
      C_PROPERTIES_STATUS.ACTIVE.id,
      C_PROPERTIES_STATUS.INNEGOTIATION.id,
      C_PROPERTIES_STATUS.DROPPED.id,
      C_PROPERTIES_STATUS.INCONFLICT.id,
      C_PROPERTIES_STATUS.PRECAPTURED.id
    ]),
    userPublicationProperty: isPropertyStatus([
      C_PROPERTIES_STATUS.ACTIVE.id,
      C_PROPERTIES_STATUS.INNEGOTIATION.id,
      C_PROPERTIES_STATUS.DROPPED.id,
      C_PROPERTIES_STATUS.INCONFLICT.id,
      C_PROPERTIES_STATUS.PRECAPTURED.id
    ]),
    dateResolveConflictProperty: isPropertyStatus([
      C_PROPERTIES_STATUS.ACTIVE.id,
      C_PROPERTIES_STATUS.INNEGOTIATION.id,
      C_PROPERTIES_STATUS.DROPPED.id,
      C_PROPERTIES_STATUS.PRECAPTURED.id
    ]),
    userResolveConflictProperty: isPropertyStatus([
      C_PROPERTIES_STATUS.ACTIVE.id,
      C_PROPERTIES_STATUS.INNEGOTIATION.id,
      C_PROPERTIES_STATUS.DROPPED.id,
      C_PROPERTIES_STATUS.PRECAPTURED.id
    ]),
    dateReactivationProperty: isPropertyStatus([
      C_PROPERTIES_STATUS.ACTIVE.id,
      C_PROPERTIES_STATUS.INNEGOTIATION.id,
      C_PROPERTIES_STATUS.DROPPED.id,
      C_PROPERTIES_STATUS.INCONFLICT.id,
      C_PROPERTIES_STATUS.PRECAPTURED.id
    ]),
    dateOutNegotiationProperty: isPropertyStatus([
      C_PROPERTIES_STATUS.INNEGOTIATION.id
    ]),
    daysOutNegotiationProperty: isPropertyStatus([
      C_PROPERTIES_STATUS.ACTIVE.id,
      C_PROPERTIES_STATUS.DROPPED.id,
      C_PROPERTIES_STATUS.INCONFLICT.id,
      C_PROPERTIES_STATUS.PENDING.id,
      C_PROPERTIES_STATUS.PRECAPTURED.id
    ]),
    dateApprovedProperty: isPropertyStatus([
      C_PROPERTIES_STATUS.ACTIVE.id,
      C_PROPERTIES_STATUS.DROPPED.id,
      C_PROPERTIES_STATUS.INCONFLICT.id,
      C_PROPERTIES_STATUS.PENDING.id,
      C_PROPERTIES_STATUS.PRECAPTURED.id
    ])
  };

  const properties = [
    {
      label: "Fecha de expiración de la propiedad",
      value: dataLog.expiresProperty,
      show: showIs.dateExpiration
    },
    {
      label: "Fecha de creación de la propiedad",
      value: dataLog.createdProperty,
      show: showIs.dateCreation
    },
    {
      label: "Creador propiedad",
      value: dataLog.propertyCreator,
      show: showIs.creatorProperty
    },
    {
      label: "Fecha de última modificación de propiedad",
      value: dataLog.lastModifiedDate,
      show: showIs.lastModificationDate
    },
    {
      label: "Último usuario que modificó la propiedad",
      value: dataLog.lastUserModifiedProperty,
      show: showIs.lastUserModificationProperty
    },
    {
      label: "Fecha de dada de baja de propiedad",
      value: dataLog.propertyDroppedDate,
      show: showIs.dateDropProperty
    },
    {
      label: "Usuario que dio de baja a la propiedad",
      value: dataLog.lastUserDroppedProperty,
      show: showIs.userDropProperty
    },
    {
      label: "Fecha de publicación de propiedad",
      value: dataLog.propertyPublicationDate,
      show: showIs.datePublicationProperty
    },
    {
      label: "Usuario que publico la propiedad",
      value: dataLog.propertyUserPublished,
      show: showIs.userPublicationProperty
    },
    {
      label: "Fecha en la que se resolvió conflicto",
      value: dataLog.propertyResolveConflictDate,
      show: showIs.dateResolveConflictProperty
    },
    {
      label: "Usuario que resolvió conflicto",
      value: dataLog.conflictResolver,
      show: showIs.userResolveConflictProperty
    },
    {
      label: "Fecha de reactivación la propiedad",
      value: dataLog.propertyReactivationDate,
      show: showIs.dateReactivationProperty
    },
    {
      label: "Fecha en la que la propiedad saldrá de negociación",
      value: dataLog.propertyOutNegotiationDate,
      show: showIs.dateOutNegotiationProperty
    },
    {
      label: "Días que la propiedad estará en negociación",
      value: dataLog.propertyOutNegotiationDays,
      show: showIs.daysOutNegotiationProperty
    },
    {
      label: "Fecha en la que se aprobó la propiedad",
      value: dataLog.activated,
      show: showIs.dateApprovedProperty
    }
  ].filter(item => item.show === true && item.value !== null);

  return properties;
};
